import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getUsers, getDailyByEmployeeMonth } from "../../services/newdaily";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import es from "date-fns/locale/es";
import moment from "moment";
import { registerLocale } from "react-datepicker";

import TimeBar from "../../components/TimeBar/TimeBar";
import Notes from "../../components/Notes/Notes";
import TaskCard from "../../components/TaskCard/TaskCard";
import TaskHeader from "../../components/TaskHeader/TaskHeader";
import TaskCreationModal from "../../modals/TaskCreationModal/TaskCreationModal";
import ViewSelector from "../../components/ViewSelector/ViewSelector";

import foldersImg from "../../assets/images/folders.png";
import PlusIcon from "../../assets/svgs/plus.svg";
import FormCheckIcon from "../../assets/svgs/formcheck.svg";
import CloseIcon from "../../assets/svgs/close.svg";

import styles from "./MainMobile.module.css";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import { SpinnerComponent } from "../../components/Spinner";

registerLocale("es", es);

const MainMobile = ({ empleado, refreshSignal, isAdmin, isMobile }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const fechaParam = searchParams.get("date");

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      return moment(fechaParam, "YYYY-MM-DD").toDate();
    }

    return new Date();
  });

  const [visibleMonthYear, setVisibleMonthYear] = useState(() => {
    const fechaParam = searchParams.get("date");
    const baseDate =
      fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()
        ? moment(fechaParam, "YYYY-MM-DD")
        : moment();

    return {
      month: baseDate.month() + 1,
      year: baseDate.year(),
    };
  });
  const [notificationMessage, setNotificationMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userNotes, setUserNotes] = useState("");
  const [proyectosTodos, setProyectosTodos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const hasAppliedInitialParams = useRef(false);

  const buildFirstDay = ({ month, year }) =>
    moment({ year, month: month - 1, day: 1 }).format("YYYY-MM-DD");

  const fetchEvents = async () => {
    try {
      if (!empleado) return;
      setIsLoading(true);

      const firstDay = buildFirstDay(visibleMonthYear);

      const data = await getDailyByEmployeeMonth({
        empleado,
        fecha: firstDay,
      });

      setProyectosTodos(Array.isArray(data?.ProyectosTODOS) ? data.ProyectosTODOS : []);

      if (data?.Daily) {
        const tareaDefs = data.Tareas || [];
        const proyectoDefs = data.Proyectos || [];

        const dailyList = Array.isArray(data?.Daily) ? data.Daily : [];
        const futureList = Array.isArray(data?.future_tasks) ? data.future_tasks : [];
        const mergedTasks = [...dailyList, ...futureList];

        const filteredEvents = mergedTasks
          .filter((task) => task.Automatica !== 1)
          .map((task) => {
            const displayStart = task.Inicio
              ? moment(`${task.Fecha} ${task.Inicio}`, "YYYY-MM-DD HH:mm:ss").toDate()
              : moment(task.Fecha, "YYYY-MM-DD").startOf("day").toDate();

            const sortStart = task.Inicio ? displayStart : new Date("2999-12-31");

            const endDate = task.Inicio
              ? moment(displayStart).add(task.Minutos || 0, "minutes").toDate()
              : displayStart;

            let tareaName = task.Tarea || "";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find((t) => t.id.toString() === task.IDTarea.toString());
              if (foundTarea) tareaName = foundTarea.tarea;
            }

            let subtareaName = task.Subtarea || "";
            if (task.IDTarea && task.IDSubtarea) {
              const foundTarea = tareaDefs.find((t) => t.id.toString() === task.IDTarea.toString());
              if (foundTarea && Array.isArray(foundTarea.subtareas)) {
                const foundSubtarea = foundTarea.subtareas.find(
                  (s) => s.id.toString() === task.IDSubtarea.toString()
                );
                if (foundSubtarea) subtareaName = foundSubtarea.subtarea;
              }
            }

            let proyectoName = task.Proyecto || "";
            if (task.IProyecto) {
              const foundProyecto = proyectoDefs.find(
                (p) => p.id.toString() === task.IProyecto.toString()
              );
              if (foundProyecto) proyectoName = foundProyecto.Proyecto;
            }

            let taskColor = task.Color || "#ccc";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find((t) => t.id.toString() === task.IDTarea.toString());
              if (foundTarea && foundTarea.color) {
                taskColor = foundTarea.color.startsWith("#") ? foundTarea.color : `#${foundTarea.color}`;
              }
            }

            return {
              id: task.id,
              title: tareaName,
              start: displayStart,
              sortStart,
              end: endDate,
              color: taskColor,
              date: moment(task.Fecha, "YYYY-MM-DD").format("YYYY-MM-DD"),
              Minutos: task.Minutos || 0,
              Tarea: tareaName,
              Subtarea: subtareaName,
              Proyecto: proyectoName,
              Inicio: task.Inicio || null,
              Hecho: task.Hecho || 0,
              IDOrigen: task.IDOrigen ?? 0,
              Origen: task.Origen || null,
              Empleado: task.Empleado,
              Notas: task.Notas,
              Descripcion: task.Descripcion,
              idTarea: task.IDTarea ?? null,
              idSubtarea: task.IDSubtarea ?? null,
              idProyecto: task.IProyecto ?? null,
              config_json: task.config_json ?? null,
              is_periodic: task.is_periodic ?? false,
              endDate: task.endDate ?? task.end_date ?? null,
              is_projection: !!task.is_projection,
              parent_periodic_id: task.parent_periodic_id ?? null,
            };
          })
          .sort((a, b) => {
            if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;
            if (a.sortStart && b.sortStart) {
              const diff = a.sortStart - b.sortStart;
              if (diff !== 0) return diff;
              return a.Tarea.localeCompare(b.Tarea);
            }
            return a.Tarea.localeCompare(b.Tarea);
          });

        setEvents(filteredEvents);
      }
    } catch (err) {
      console.error("Error al cargar tareas:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (modalIsOpen) return;
    fetchEvents();
  }, [refreshSignal, visibleMonthYear, modalIsOpen]);

  useEffect(() => {
    const formatted = moment(selectedDate).format("YYYY-MM-DD");
    const filtered = events.filter((task) => task.date === formatted);
    setSelectedTasks(filtered);
  }, [events, selectedDate]);

  useEffect(() => {
    if (!empleado) return;

    const fetchUser = async () => {
      try {
        const data = await getUsers();

        if (data?.Usuarios && Array.isArray(data.Usuarios)) {
          const user = data.Usuarios.find(
            (u) => u.id.toString() === empleado.toString()
          );

          if (user) {
            setUserName(user.Nombre);
            setUserNotes(user.Notas ?? "");
          }
        }
      } catch (error) {
        console.error("Error al obtener nombre de usuario:", error);
      }
    };

    fetchUser();
  }, [empleado]);

  useEffect(() => {
    const fechaParam = searchParams.get("date");
    const taskIdParam = searchParams.get("task_id");

    let usedParams = false;

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      const parsedDate = moment(fechaParam, "YYYY-MM-DD");

      setSelectedDate(parsedDate.toDate());
      setVisibleMonthYear({
        month: parsedDate.month() + 1,
        year: parsedDate.year(),
      });

      usedParams = true;
    }

    if (taskIdParam) {
      setHighlightedTaskId(String(taskIdParam));
      usedParams = true;
    } else {
      setHighlightedTaskId(null);
    }

    if (usedParams && !hasAppliedInitialParams.current) {
      hasAppliedInitialParams.current = true;
      navigate(".", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!highlightedTaskId || !selectedTasks.length) return;

    const existsInSelectedDate = selectedTasks.some(
      (task) => String(task.id) === String(highlightedTaskId)
    );

    if (!existsInSelectedDate) return;

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${highlightedTaskId}"]`);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [highlightedTaskId, selectedTasks]);


  const updateTaskInState = (taskId, newHecho) => {
    setEvents((prev) => prev.map((t) => (t.id === taskId ? { ...t, Hecho: newHecho } : t)));
    setSelectedTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, Hecho: newHecho } : t)));
  };

  const sortedTasks = [...selectedTasks].sort((a, b) => {
    if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;
    const hasOrigenA = a.Origen && a.Origen !== userName;
    const hasOrigenB = b.Origen && b.Origen !== userName;
    if (hasOrigenA !== hasOrigenB) return hasOrigenB - hasOrigenA;
    if (a.sortStart && b.sortStart) return a.sortStart - b.sortStart;
    if (a.Minutos !== b.Minutos) return b.Minutos - a.Minutos;
    return a.Tarea.localeCompare(b.Tarea);
  });

  const handleTaskSuccess = (message) => {
    setNotificationMessage(message);
    setTimeout(() => setNotificationMessage(""), 3000);
    fetchEvents();
  };

  const handleCloseBanner = () => {
    setNotificationMessage("");
  };

  return (
    <>
      <div className={styles.datePickerContainer}>
        <div className={styles.datePickerWrapper}>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            locale="es"
            inline
            calendarClassName={styles.customCalendar}
            onMonthChange={(date) => {
              setVisibleMonthYear({
                month: moment(date).month() + 1,
                year: moment(date).year(),
              });
            }}
            onYearChange={(date) => {
              setVisibleMonthYear({
                month: moment(date).month() + 1,
                year: moment(date).year(),
              });
            }}
          />
        </div>
      </div>

      <div className={styles.mainMobileContainer}>
        {notificationMessage && (
          <div className={styles.notificationBanner}>
            <div className={styles.iconContainer}>
              <img src={FormCheckIcon} alt="Check icon" className={styles.iconCheck} />
            </div>
            {notificationMessage}
            <button className={styles.closeButton} onClick={handleCloseBanner}>
              <img src={CloseIcon} alt="Close icon" className={styles.closeIcon} />
            </button>
          </div>
        )}

        <ViewSelector isMobile={isMobile} />
        <TaskHeader
          selectedDate={moment(selectedDate).format("YYYY-MM-DD")}
          onSuccess={handleTaskSuccess}
          userName={userName}
          empleado={empleado}
          isMobile={isMobile}
          proyectosTodos={proyectosTodos}
        />

        {selectedTasks.length > 0 && (
          <TimeBar
            tasks={selectedTasks}
            selectedDate={moment(selectedDate).format("YYYY-MM-DD")}
            isMobile={isMobile}
          />
        )}

        <div className={styles.timebarDivider} />

        <Notes initialNotes={userNotes} userId={empleado} isMobile={isMobile} />

        <div className={styles.lowerSection}>
          <div className={styles.taskListContainer}>
            {isLoading ? (
              <div className={styles.spinnerContainer}>
                <SpinnerComponent />
              </div>
            ) : selectedTasks.length > 0 ? (
              <OverlayScrollbarsComponent style={{ height: "100%", width: "100%" }}>
                <div className={styles.taskList}>
                  {sortedTasks.map((task) => (
                    <div key={task.id} data-task-id={task.id}>
                      <TaskCard
                        task={task}
                        updateTaskInState={updateTaskInState}
                        onSuccess={handleTaskSuccess}
                        empleado={empleado}
                        isMobile={isMobile}
                        proyectosTodos={proyectosTodos}
                      />
                    </div>
                  ))}
                </div>
              </OverlayScrollbarsComponent>
            ) : (
              <div className={styles.noTaskContainer}>
                <img src={foldersImg} alt="No tasks" />
                <p>No tienes tareas asignadas</p>
              </div>
            )}
          </div>
        </div>

        <button
          className={styles.floatingAddButton}
          onClick={() => setModalIsOpen(true)}
          aria-label="Añadir tarea"
        >
          <img src={PlusIcon} alt="Añadir" className={styles.floatingIcon} />
        </button>

        <TaskCreationModal
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          onSuccess={handleTaskSuccess}
          defaultDate={moment(selectedDate).format("YYYY-MM-DD")}
          empleado={empleado}
          isMobile={isMobile}
          proyectosTodos={proyectosTodos}
        />
      </div>
    </>
  );

};

export default MainMobile;
